import { Check, Circle, Film, Image as ImageIcon } from 'lucide-react';

import { cn } from '@/lib/cn';
import { getProviderMeta } from '@/lib/editorCapabilities';

import type { WizardState } from '../types';

interface Props {
  state: WizardState;
  className?: string;
}

/**
 * Read-only summary of the choices made so far. Rendered as a sidebar on
 * wide screens; on narrow viewports the parent shell hides it (the user
 * has the progress dots + step name as their orientation anchor).
 */
export function PreviewSidebar({ state, className }: Props) {
  const checks: ReadonlyArray<{ label: string; done: boolean }> = [
    { label: 'Concept', done: !!state.prompt || !!state.sourceImageDataUrl },
    { label: 'Render setup', done: !!state.model || !!state.aspectRatio },
    { label: 'Image generated', done: !!state.avatarImageUrl },
    { label: 'Motion described', done: state.motionPrompt.trim().length >= 3 },
    { label: 'Engine picked', done: !!state.videoProvider },
    { label: 'Clip ready', done: state.clipStatus === 'ready' },
  ];

  const provider = state.videoProvider ? getProviderMeta(state.videoProvider) : null;

  return (
    <aside className={cn('flex flex-col gap-3 rounded-2xl bg-bg-secondary p-4', className)}>
      <div className="flex flex-col gap-1">
        <p className="text-[11px] font-semibold uppercase text-fg-muted">Draft</p>
        <h3 className="line-clamp-1 text-sm font-bold text-fg">
          {state.title || state.prompt.slice(0, 40) || 'Untitled avatar'}
        </h3>
      </div>

      {/* Thumbnail (avatar after Step 3) or clip poster (after Step 6). */}
      <div className="aspect-square overflow-hidden rounded-xl border border-black/40 bg-black">
        {state.clipUrl && state.clipStatus === 'ready' ? (
          <div className="relative flex h-full w-full items-center justify-center">
            <Film aria-hidden className="absolute inset-0 m-auto h-6 w-6 text-brand opacity-60" />
            <video
              src={state.clipUrl}
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-cover"
            />
          </div>
        ) : state.avatarImageUrl ? (
          <img src={state.avatarImageUrl} alt="" className="h-full w-full object-cover" />
        ) : state.sourceImageDataUrl ? (
          <img src={state.sourceImageDataUrl} alt="" className="h-full w-full object-cover opacity-70" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-fg-muted">
            <ImageIcon aria-hidden className="h-8 w-8 opacity-40" />
          </div>
        )}
      </div>

      {/* Checklist */}
      <ul className="flex flex-col gap-1.5 text-xs">
        {checks.map(c => (
          <li key={c.label} className="flex items-center gap-2">
            {c.done ? (
              <Check aria-hidden className="h-3.5 w-3.5 text-brand" />
            ) : (
              <Circle aria-hidden className="h-3.5 w-3.5 text-fg-muted/50" />
            )}
            <span className={cn(c.done ? 'text-fg' : 'text-fg-muted')}>{c.label}</span>
          </li>
        ))}
      </ul>

      {/* Engine snapshot */}
      {provider && (
        <div className="rounded-xl bg-bg-tertiary p-2.5 text-[11px] text-fg-muted">
          <p>
            <span className="font-semibold text-fg">{provider.label}</span>
            {' · '}
            {state.durationSec}s
            {' · '}
            {state.resolution}
            {state.variants > 1 && ` · ${state.variants}×`}
          </p>
          {state.videoTransparency !== 'none' && (
            <p className="mt-0.5">Transparent ({state.videoTransparency})</p>
          )}
        </div>
      )}
    </aside>
  );
}
